import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import {
    ImageContentSchema,
    UpsertContentDTO,
    UpsertContentDTOSchema,
} from "src/app/types/content";
import { Order, WritingType } from "src/app/types/orders/order";
import { ContentTransport } from "src/app/transports/content";
import { ReviewTextOrderForm, SimpleModal } from "../../components";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { useState } from "react";
import { apiToFormikErrors } from "src/app/utils/formHelpers";
import { InferType, array, number, object, string } from "yup";
import { useAuth } from "src/app/utils/useAuth";
import { TDocMetrics } from "src/app/utils/textCheckHandler/handleData";
import { Button } from "react-bootstrap";
import { IActionsModal } from "src/app/types/actionsModal";

import SimpleRating from "../../components/form/SimpleRating";

import { RateResonFormContainer } from "..";
import reason from "src/app/utils/RateReasons/RateReasons.json";
import { useGetOrderById } from "src/app/hooks";
import Pacman from "src/app/components/layout/Pacman";

const ReviewTextOrderFormContainer = (props: IActionsModal<Order>) => {
    const { handleClose = () => {}, entity, updateGridOnSave = () => {} } = props;
    const { currentUser } = useAuth();
    const { formatMessage } = useIntl();

    const [loading, setLoading] = useState(false);
    const [showRateModal, setShowRateModal] = useState(false);
    const [importantReason, setImportantReason] = useState(false);
    const [confirmationLabel, setConfirmationLabel] = useState("save");
    const [docMetrics, setDocMetrics] = useState<TDocMetrics>();
    const [rateChanged, setRateChanged] = useState<boolean>(false);
    const [disableConfirmationButton, setDisableConfirmationButton] = useState(true);

    const { order, loading: orderLoading } = useGetOrderById(entity?.id);

    const handleDisableButton = (disable: boolean) => {
        if (!disable && rateChanged) {
            setDisableConfirmationButton(disable);
        } else {
            setDisableConfirmationButton(true);
        }
    };

    let validationSchema: any = UpsertContentDTOSchema.concat(
        object({
            images: array()
                .of(ImageContentSchema)
                .min(1, "error.content.image.required")
                .required("error.content.image.required"),
            rate: number().required().default(0),
            commentFromProofreader: string().when("rate", {
                is: (rate: number) => rate < 5 && order?.writerNeeded,
                then: (schema) => schema.required("error.content.commentFromProofreader.required"),
                otherwise: (schema) => schema.nullable(),
            }),
        })
    );

    if (order?.content?.writingType === WritingType.AI_AUTO) {
        validationSchema = validationSchema.omit(["metaTitle", "metaDescription"]);
    }

    type validationDTO = InferType<typeof validationSchema>;

    const handleSubmit = async (_values: validationDTO, formikHelpers: FormikHelpers<any>) => {
        setLoading(true);

        const currentValues = {
            ..._values,
            wordCount: docMetrics?.numberOfWords,
            orderId: order?.id,
        };

        try {
            const res = await ContentTransport.saveReviewedContentInOrder(
                currentValues,
                _values.id
            );
            const newEntity = {
                ...order,
                content: res,
            };

            await updateGridOnSave(newEntity, false, true);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleClose();
        } catch (error: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
            apiToFormikErrors(error?.response, formikHelpers);
        } finally {
            setLoading(false);
        }
    };

    const handleReturnText = async (_values: validationDTO) => {
        setLoading(true);
        try {
            const res = await ContentTransport.returnText(_values);
            const newEntity = {
                ...order,
                content: res,
            };

            await updateGridOnSave(newEntity, false, true);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleClose();
        } catch (error: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    };

    const reasonConfirmation = (formikProps: FormikProps<any>) => {
        const reasonsSelected = formikProps.values.reason;
        let hasImportanceHigh = false;
        let currentRate = 5;
        let commentFromProofreader = "";
        let importance = "LOW";

        if (reasonsSelected) {
            const reasonsGroups = Object.keys(reasonsSelected);
            reasonsGroups.forEach((reasonsGroupName) => {
                importance = reason[reasonsGroupName as keyof typeof reason].importance;
                const reasonGroup = reasonsSelected[reasonsGroupName];
                const reasonsKeys = Object.keys(reasonGroup);
                reasonsKeys.forEach((reasonKey) => {
                    if (reasonGroup[reasonKey]) {
                        const reasonEl = reason[
                            reasonsGroupName as keyof typeof reason
                        ].reasons.find((el) => el.value === reasonKey) || { reason: "" };
                        commentFromProofreader += `${reasonEl.reason}\n `;
                        if (importance === "HIGH" || (importance === "LOW" && hasImportanceHigh)) {
                            hasImportanceHigh = true;
                            currentRate = 1;
                        } else {
                            currentRate -= 0.5;
                        }
                    }
                });
            });
            if (currentRate < 2 && !hasImportanceHigh) {
                currentRate = 2;
            }

            if (hasImportanceHigh) {
                setImportantReason(true);
            } else {
                setImportantReason(false);
            }
        }
        formikProps.setFieldValue("rate", Math.floor(currentRate));
        formikProps.setFieldValue("commentFromProofreader", commentFromProofreader);
        setRateChanged(true);
        setShowRateModal(false);
    };

    const Footer = ({ _values }: { _values: validationDTO }) => (
        <div className="w-100 d-flex justify-content-end gap-2">
            {order?.content?.writingType === WritingType.AI_AUTO ? (
                <>
                    <Button
                        onClick={() => {
                            setShowRateModal(true);
                        }}
                    >
                        {formatMessage({ id: "common.field.rate" })}
                    </Button>
                </>
            ) : (
                <>
                    <label>{formatMessage({ id: "common.field.rate" })}:</label>
                    <SimpleRating name="rate" disable={!!order?.rate} />
                </>
            )}
            {order?.rate && order.content?.writingType !== WritingType.AI_AUTO && (
                <>
                    <Button
                        onClick={() => {
                            handleReturnText(_values);
                        }}
                    >
                        {formatMessage({ id: "common.action.returnText" })}
                    </Button>
                </>
            )}
            <div />
        </div>
    );

    if (!order)
        return (
            <SimpleModal
                entityName={formatMessage({ id: "common.entity.viewText" })}
                handleClose={handleClose}
                loading={loading}
                formId=""
                showModal={true}
            >
                <Pacman />
            </SimpleModal>
        );
    return (
        <Formik
            initialValues={
                order?.content
                    ? ContentTransport.formTransformer(
                          order?.content,
                          order.rate,
                          order.commentFromProofreader
                      )
                    : validationSchema.getDefault()
            }
            validationSchema={validationSchema}
            onSubmit={(values: object, formikHelpers: FormikHelpers<object>) => {
                handleSubmit(values as validationDTO, formikHelpers);
            }}
        >
            {(formikProps: FormikProps<UpsertContentDTO>) => {
                const rate: number | unknown = formikProps.getFieldMeta("rate").value;
                const rateTouched: number | unknown = formikProps.getFieldMeta("rate").touched;

                if (rate != undefined && typeof rate == "number" && rate <= 2) {
                    if (
                        confirmationLabel === "save" &&
                        order?.content?.writingType !== WritingType.AI_AUTO
                    ) {
                        setConfirmationLabel("returnToWriterNeed");
                    }
                    if (
                        confirmationLabel === "save" &&
                        order?.content?.writingType === WritingType.AI_AUTO
                    ) {
                        setConfirmationLabel("returnText");
                    }
                } else {
                    setConfirmationLabel("save");
                }
                if (rateTouched) {
                    setRateChanged(true);
                }

                return (
                    <>
                        <Form id="viewTextForm">
                            <SimpleModal
                                entityName={formatMessage({ id: "common.entity.viewText" })}
                                handleClose={handleClose}
                                disableConfirmation={disableConfirmationButton}
                                buttonConfirmationLabel={formatMessage({
                                    id: `common.action.${confirmationLabel}`,
                                })}
                                loading={loading || orderLoading}
                                formId="viewTextForm"
                                showModal={true}
                                modalProps={{ fullscreen: true }}
                                footerProps={{ style: { padding: "10px 1.75rem" } }}
                                headerProps={{ style: { padding: "10px 1.75rem" } }}
                                bodyProps={{ style: { padding: "10px 1.75rem" } }}
                                FooterLeftComponent={
                                    <Footer _values={formikProps.values as validationDTO} />
                                }
                            >
                                <ReviewTextOrderForm
                                    entity={order}
                                    importantReason={importantReason}
                                    userRole={currentUser.role}
                                    handleDisableButton={handleDisableButton}
                                    docMetrics={docMetrics}
                                    setDocMetrics={setDocMetrics}
                                />
                            </SimpleModal>
                        </Form>
                        <SimpleModal
                            entityName={formatMessage({ id: "common.entity.viewText" })}
                            handleClose={() => setShowRateModal(false)}
                            buttonConfirmationAction={() => {
                                reasonConfirmation(formikProps);
                            }}
                            loading={loading}
                            formId=""
                            showModal={showRateModal}
                        >
                            <RateResonFormContainer />
                        </SimpleModal>
                    </>
                );
            }}
        </Formik>
    );
};

export default ReviewTextOrderFormContainer;
